<template>
  <div class="flex-fill">
    <div class="cart-assort-selector">
      <el-button plain class="btn-block" :style="{ padding: '5px 10px' }" @click="onClick">
        <div class="w-100 d-flex justify-content-between">
          <div class="text-wrap text-left" :style="{ lineHeight: '1.3rem' }">{{ data?.assort_name }}</div>
          <i class="fa fa-chevron-down el-icon--right text-primary" aria-hidden="true"></i>
        </div>
      </el-button>
    </div>

    <el-drawer v-model="drawer" :title="t('carts.options')" :size="isMobile ? '95%' : 700" :append-to-body="true">
      
      <div class="d-flex w-100">
        <el-image :src="formState.image_url" :alt="data?.product_name"
          style="width: 100px; height: 100px; border: 1px solid #eef6ff;" class="rounded mr-2">
          <template #error>
          </template>
        </el-image>
        <div class="flex-fill">
          <el-tag v-if="formState?.hasErrorStatus" type="danger" effect="dark" class="mr-1">
            {{ formState.hasErrorStatus }}
          </el-tag>
          <div>{{ data?.product_name }}</div>
          <div v-if="!hiddenPrice" class="d-flex flex-wrap align-items-center">
            <div class="text-accent mr-2">
              {{ data?.symbol }}
              {{ n(formState.discounted_price || 0) }}
            </div>
        
            <div v-if="!!formState.comm_price" class="text-muted">
              <s>
                {{ t("carts.commPrice") }}:
                {{ data?.symbol }}
                {{ n(formState.comm_price || 0) }}
              </s>
            </div>
          </div>
        </div>
      </div>
      
      <el-divider />
      <div class="text-muted mb-3">{{ t("products.assorts") }}:</div>

      <div v-loading="loading">
        <el-radio-group v-model="formState.store_assort_id" size="large" class="d-flex flex-column align-items-start"
          @change="handleChange">
          <el-radio 
            v-for="assort in item?.assorts" 
            :key="assort.id" 
            :label="assort.id" 
            class="text-wrap"
            style="height: auto; min-height: 40px; width: 100%;" 
            border>
            <div class="d-flex flex-column">
              <div v-if="dt(assort.label_translations)" class="mb-1 mt-2">
                <span class="badge badge-pill badge-accent assort-label text-left">
                  <span class="text-truncate-3">{{ dt(assort.label_translations) }}</span>
                </span>
              </div>

              <div>
                {{ dt(assort?.name_translations) }}
              </div>
              <div v-if="!hiddenPrice && assort.sale_price" class="text-lg text-accent sale-price"
                style="font-size: 24px; min-width: 75px; white-space: nowrap;">
                {{ `${assort.symbol}${$n(assort.sale_price || 0)}` }}
              </div>
              
            </div>
          </el-radio>
        </el-radio-group>
      </div>
      <template #footer>
        <el-button type="primary" size="large" :loading="loading" :disabled="!!formState.hasErrorStatus" class="btn-block"
          @click="handleSave">
          {{ t("carts.confirmChangeAssort") }}
        </el-button>
      </template>
    </el-drawer>
  </div>
</template>

<script setup>
import { reactive, ref, computed } from 'vue'
import { useI18n } from "vue-i18n"
import { breakpoint, notificationErrors, dataTranslations as dt, lookupAliasTranslations } from "../../utils"
import {
  _api_getPromotionProducts,
  _api_getProduct,
} from "../../api/index.js";

const props = defineProps({
  data: { type: Object, required: true },
  storeData: { type: Object, required: true },
  dealer: { type: Object, default: () => { } },
  isSubscribe: { type: Boolean, default: false },
})
const emit = defineEmits(['change'])

const { t, n } = useI18n()

const isMobile = computed(() => breakpoint.value.xs || breakpoint.value.sm)
const isPromotion = computed(() => props.data?.event_group !== null)
const hiddenPrice = computed(() => !!props.storeData?.settings?.hidden_price)
const hiddenCart = computed(() => !!props.storeData?.settings?.hidden_cart)
const hiddenContact = computed(() => !!props.storeData?.settings?.hidden_contact)
const aliasTranslations = computed(() => lookupAliasTranslations(props.storeData?.alias_translations, locale.value))

const drawer = ref(false)
const loading = ref(false)
const item = ref(null)
const formState = reactive({
  store_assort_id: null,
  comm_price: null,
  discounted_price: null,
  image_url: null,
  ptype: null,
  has_error: null,
})

const hasErrorStatus = (item) => {
  // console.debug("hasErrorStatus: ", item)
  if (item.status != 'shelves') {
    return t("carts.ProductOff")
  } else if (!item.inventory || !item.max_order_quantity) {
    return "已售完"
  } else if (item.quantity > item.max_order_quantity) {
    return `已超過最大訂購上限 ${item.max_order_quantity}`
  }
  return null
}
const hasWarningStatus = (item) => {
  if (item.inventory < 10) {
    return t("carts.ProductRemaining", [item.inventory])
  }
  return null
}
const onClick = async () => {
  formState.store_assort_id = props.data?.store_assort_id
  formState.discounted_price = props.data?.discounted_price
  formState.comm_price = props.data?.comm_price
  formState.image_url = props.data?.image_url
  formState.ptype = props.data?.ptype
  formState.hasErrorStatus = props.data?.hasErrorStatus
  drawer.value = true
  loading.value = true
  
  try {
    item.value = null
    if (isPromotion.value) {
      const params = {
        store_product_id: props.data?.store_product_id,
        fields: "assorts,assorts.image_url",
      }
      const resp = await _api_getPromotionProducts(props.data?.event_group?.event_id, params)
      if (resp?.data !== undefined && !!resp?.data.length) {
        item.value = resp?.data[0]
      }
    } else {
      item.value = await _api_getProduct(props.data?.store_product_id)
    }
  } catch (error) {
    notificationErrors(error)
  } finally {
    loading.value = false
  }
}

const handleChange = (val) => {
  const assort = item.value?.assorts.find((el) => el.id === val)
  // console.debug("handleChange: ", assort)
  formState.hasErrorStatus = hasErrorStatus(assort)
  if (isPromotion.value) {
    formState.comm_price = assort?.comm_price_currencies[props.storeData?.currency]
    const sale_price = assort?.sale_price_currencies[props.storeData?.currency]
    formState.discounted_price = assort?.discounted_price ? assort?.discounted_price : sale_price
    if (assort?.image_url) {
      formState.image_url = assort?.image_url
    }
  } else {
    formState.comm_price = assort?.comm_price
    if (assort?.media_id) {
      formState.image_url = assort?.media?.url
    }

    if (props.storeData?.type === "wholesale") {
      formState.discounted_price = props.isSubscribe ? assort?.sale_price : assort?.comm_price
    } else if (props.storeData?.type === "deal") {
      let discount_percentage = props.dealer?.level_info?.discount_percentage
      if (assort?.deal_settings?.discount_type == 'custom') {
        discount_percentage = assort?.deal_settings?.discount_percentage
      }
      formState.comm_price = assort?.sale_price
      formState.discounted_price = assort?.sale_price * (100 - discount_percentage) / 100
    } else {
      formState.discounted_price = assort?.sale_price
    }
  }
}
const handleSave = () => {
  const assort = item.value?.assorts.find((el) => el.id === formState.store_assort_id)
  const params = {
    cart_id: props.data?.cart_id,
    ptype: props.data?.ptype,
    quantity: props.data?.quantity,
    event_id: props.data?.event_group?.event_id,
    event_type: props.data?.event_group?.event_type,
    store_product_id: props.data?.store_product_id,
    max_order_quantity: assort?.max_order_quantity,
    min_order_quantity: assort?.min_order_quantity,
    old_store_assort_id: props.data?.store_assort_id,
    new_store_assort_id: assort.id,
  }
  // console.debug("handleSave: ", params)
  // 有變更商品組合
  if (formState.store_assort_id !== props.data?.store_assort_id) {
    emit("change", params)
  }
  drawer.value = false
}

</script>
<style>
.cart-assort-selector .el-button {
  height: auto !important;
}

.cart-assort-selector .el-button>span {
  width: 100% !important;
}
</style>